import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, ScatterChart, Scatter, LabelList, Legend } from 'recharts';
import { formatValue } from '@/lib/formatters';

interface VisualRendererProps {
    type: 'bar' | 'line' | 'area' | 'pie' | 'scatter';
    data: any[];
    xAxisKey?: string;
    yAxisKey?: string;
    color?: string;
    showGrid?: boolean;
    showLabels?: boolean;
    groupKey?: string;
    showLegend?: boolean;
    formatSettings?: {
        formatType?: 'none' | 'currency' | 'percent';
        currency?: string;
        decimals?: number;
        units?: 'auto' | 'none' | 'K' | 'M' | 'B';
    };
}

const COLORS = ['#0F52BA', '#438AF6', '#3B82F6', '#60A5FA', '#93C5FD', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6'];

export const VisualRenderer = ({ type, data, xAxisKey, yAxisKey, color = "#0F52BA", showGrid = false, showLabels = false, groupKey, showLegend = false, formatSettings }: VisualRendererProps) => {

    if (!data || data.length === 0) return <div className="flex items-center justify-center h-full text-gray-400 text-xs">No Data</div>;

    const xKey = xAxisKey || Object.keys(data[0])[0];
    const yKey = yAxisKey || Object.keys(data[0]).find(k => typeof data[0][k] === 'number') || Object.keys(data[0])[1];

    // Helper for formatting
    const format = (val: any) => {
        if (!formatSettings) return val;
        // mock WidgetStyle structure for formatValue compatibility
        // We ensure a minimum valid WidgetStyle object is passed
        return formatValue(val, {
            type: 'scalar', // dummy
            title: '',
            ...formatSettings
        } as any);
    };

    // Helper for pivoting data if groupKey is present
    const transformData = (rawData: any[]) => {
        if (!groupKey) return { isGrouped: false, data: rawData, keys: [yKey] };

        const groups = new Set<string>();
        const groupedMap: Record<string, any> = {};

        rawData.forEach(row => {
            const xVal = row[xKey];
            const gVal = row[groupKey]; // The group/legend value
            const yVal = parseFloat(row[yKey] || '0');

            if (gVal) groups.add(String(gVal));

            if (!groupedMap[xVal]) {
                groupedMap[xVal] = { [xKey]: xVal, ...row };
            }
            // Pivot: each group becomes a key
            if (gVal) groupedMap[xVal][String(gVal)] = yVal;
        });

        return {
            isGrouped: true,
            data: Object.values(groupedMap),
            keys: Array.from(groups)
        };
    };

    const { isGrouped, data: chartData, keys: seriesKeys } = transformData(data);

    const commonAxisProps = {
        style: { fontSize: '10px' },
        tickFormatter: (val: any) => format(val)
    }

    // Tooltip Content Customization
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-2 border border-slate-200 shadow-lg rounded text-xs">
                    <p className="font-semibold mb-1">{label}</p>
                    {payload.map((p: any, idx: number) => (
                        <div key={idx} className="flex items-center gap-2" style={{ color: p.color }}>
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                            <span>{p.name}: {format(p.value)}</span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    if (type === 'bar') {
        return (
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                    {showGrid && <CartesianGrid strokeDasharray="3 3" />}
                    <XAxis dataKey={xKey} style={{ fontSize: '10px' }} tickFormatter={(val) => val?.toString().substring(0, 15)} />
                    <YAxis {...commonAxisProps} width={40} />
                    <Tooltip content={<CustomTooltip />} />
                    {showLegend && <Legend wrapperStyle={{ fontSize: '10px' }} />}

                    {isGrouped ? (
                        seriesKeys.map((key, idx) => (
                            <Bar key={key} dataKey={key} fill={COLORS[idx % COLORS.length]}>
                                {showLabels && <LabelList dataKey={key} position="top" style={{ fontSize: '10px' }} formatter={format} />}
                            </Bar>
                        ))
                    ) : (
                        <Bar dataKey={yKey} fill={color}>
                            {showLabels && <LabelList dataKey={yKey} position="top" style={{ fontSize: '10px' }} formatter={format} />}
                        </Bar>
                    )}
                </BarChart>
            </ResponsiveContainer>
        );
    }

    if (type === 'line') {
        return (
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                    {showGrid && <CartesianGrid strokeDasharray="3 3" />}
                    <XAxis dataKey={xKey} style={{ fontSize: '10px' }} tickFormatter={(val) => val?.toString().substring(0, 15)} />
                    <YAxis {...commonAxisProps} width={40} />
                    <Tooltip content={<CustomTooltip />} />
                    {showLegend && <Legend wrapperStyle={{ fontSize: '10px' }} />}

                    {isGrouped ? (
                        seriesKeys.map((key, idx) => (
                            <Line key={key} type="monotone" dataKey={key} stroke={COLORS[idx % COLORS.length]} strokeWidth={2} dot={false}>
                                {showLabels && <LabelList dataKey={key} position="top" style={{ fontSize: '10px' }} formatter={format} />}
                            </Line>
                        ))
                    ) : (
                        <Line type="monotone" dataKey={yKey} stroke={color} strokeWidth={2} dot={false}>
                            {showLabels && <LabelList dataKey={yKey} position="top" style={{ fontSize: '10px' }} formatter={format} />}
                        </Line>
                    )}
                </LineChart>
            </ResponsiveContainer>
        );
    }

    if (type === 'area') {
        return (
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                    {showGrid && <CartesianGrid strokeDasharray="3 3" />}
                    <XAxis dataKey={xKey} style={{ fontSize: '10px' }} tickFormatter={(val) => val?.toString().substring(0, 15)} />
                    <YAxis {...commonAxisProps} width={40} />
                    <Tooltip content={<CustomTooltip />} />
                    {showLegend && <Legend wrapperStyle={{ fontSize: '10px' }} />}

                    {isGrouped ? (
                        seriesKeys.map((key, idx) => (
                            <Area key={key} type="monotone" dataKey={key} stroke={COLORS[idx % COLORS.length]} fill={COLORS[idx % COLORS.length]} fillOpacity={0.3}>
                                {showLabels && <LabelList dataKey={key} position="top" style={{ fontSize: '10px' }} formatter={format} />}
                            </Area>
                        ))
                    ) : (
                        <Area type="monotone" dataKey={yKey} stroke={color} fill={color} fillOpacity={0.3}>
                            {showLabels && <LabelList dataKey={yKey} position="top" style={{ fontSize: '10px' }} formatter={format} />}
                        </Area>
                    )}
                </AreaChart>
            </ResponsiveContainer>
        );
    }

    if (type === 'pie') {
        return (
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Tooltip />
                    {showLegend && <Legend wrapperStyle={{ fontSize: '10px' }} />}
                    <Pie
                        data={data}
                        dataKey={yKey}
                        nameKey={xKey}
                        cx="50%"
                        cy="50%"
                        outerRadius="80%"
                        fill="#8884d8"
                        label={({ cx, cy, midAngle, innerRadius, outerRadius, value, index }) => {
                            return showLabels ? `${data[index][xKey]}: ${format(value)}` : '';
                        }}
                    >
                        {data.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                </PieChart>
            </ResponsiveContainer>
        );
    }

    if (type === 'scatter') {
        return (
            <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                    {showGrid && <CartesianGrid />}
                    <XAxis type="category" dataKey={xKey} name={xKey} style={{ fontSize: '10px' }} />
                    <YAxis type="number" dataKey={yKey} name={yKey} {...commonAxisProps} width={40} />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                    {showLegend && <Legend wrapperStyle={{ fontSize: '10px' }} />}
                    {/* Scatter grouping is complex, skipping for now unless groupKey is used to color differently */}
                    <Scatter name="Data" data={data} fill={color}>
                        {showLabels && <LabelList dataKey={yKey} position="top" style={{ fontSize: '10px' }} formatter={format} />}
                    </Scatter>
                </ScatterChart>
            </ResponsiveContainer>
        );
    }

    return null;
};
